import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useSession } from '../session'
import {
  ArrowRight,
  CalendarDays,
  CircleAlert,
  FilePlus2,
  FileText,
  HandCoins,
  PackageMinus,
  Receipt,
  ShoppingCart,
  Store,
  TrendingUp,
  Truck,
  Wallet,
} from 'lucide-react'
import { getApAging, getArAging, getKpis, getLowStock, getTopProducts, getTrendDaily } from '../api/dashboard'
import { getCompany } from '../api/settings'
import MultiBarChart from '../components/MultiBarChart'
import { Card, Tile, useWidth } from '../components/Summary'
import { companyNow, currencyCode, money } from '../utils/format'

// The landing page after sign-in, after PropertyBuk's Overview: the company,
// the few numbers that matter today, what needs attention, and short lists
// that link through to the full pages. Detailed analysis lives on
// /dashboard/analysis.

const LOW_STOCK_THRESHOLD = 5
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const pad = (n) => String(n).padStart(2, '0')
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

function shiftDays(d, days) {
  const next = new Date(d)
  next.setDate(next.getDate() + days)
  return next
}

/** The same day last month, clamped to that month's length. */
function sameDayLastMonth(d) {
  const last = new Date(d.getFullYear(), d.getMonth(), 0).getDate()
  return new Date(d.getFullYear(), d.getMonth() - 1, Math.min(d.getDate(), last))
}

/** "Up 12% on yesterday", or a plain fallback when there is nothing to compare with. */
function comparison(current, previous, against) {
  const now = Number(current || 0)
  const then = Number(previous)
  if (!Number.isFinite(then) || Math.abs(then) < 1e-9) return { note: `Nothing ${against} to compare with` }
  const pct = Math.round(((now - then) / Math.abs(then)) * 100)
  if (pct === 0) return { note: `Level with ${against}` }
  return { note: `${pct > 0 ? 'Up' : 'Down'} ${Math.abs(pct)}% on ${against}`, tone: pct > 0 ? 'up' : 'down' }
}

/** Daily trend rows summed into calendar months. */
function byMonth(trend) {
  const months = new Map()
  ;(trend?.labels || []).forEach((day, i) => {
    const key = String(day).slice(0, 7)
    const m = months.get(key) || { revenue: 0, gross: 0 }
    m.revenue += Number(trend.revenue?.[i] || 0)
    m.gross += Number(trend.gross_profit?.[i] || 0)
    months.set(key, m)
  })
  const keys = [...months.keys()].sort()
  return {
    labels: keys.map((k) => MONTHS[Number(k.slice(5, 7)) - 1]),
    revenue: keys.map((k) => Math.round(months.get(k).revenue * 100) / 100),
    gross: keys.map((k) => Math.round(months.get(k).gross * 100) / 100),
  }
}

// Chart axis: short amounts in the company's currency, e.g. ₦33.2K.
const compact = (n) => {
  try {
    return Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: currencyCode(), currencyDisplay: 'narrowSymbol', notation: 'compact', maximumFractionDigits: 1 })
  } catch {
    return Number(n || 0).toLocaleString('en-US', { notation: 'compact', maximumFractionDigits: 1 })
  }
}

export default function Dashboard({ roles = [], approvals = {} }) {
  const isAdmin = roles.includes('admin')
  const { has } = useSession()
  // Money numbers by role; the books post on every plan, so sales and what's
  // owed show without Accounting. Only ledger-only pieces need the plan to
  // have Accounting (books).
  const canSeeFinance = isAdmin || roles.includes('accounting')
  const books = has('accounting')
  const canSeeSupply = (isAdmin || roles.includes('purchase') || roles.includes('inventory')) && has('inventory')
  const canSeeVendors = canSeeFinance && has('purchase')
  const canSeeCustomers = canSeeFinance && has('invoices|jobs')
  const canSeeStock = isAdmin || roles.includes('inventory')
  const canSell = isAdmin || roles.includes('sales')
  const canBuy = isAdmin || roles.includes('purchase')
  const canSpend = isAdmin || roles.includes('operating_cost')

  const [data, setData] = useState(null)
  const [company, setCompany] = useState(null)

  useEffect(() => {
    getCompany().then((c) => setCompany(c || null)).catch(() => {})
  }, [])

  useEffect(() => {
    let cancelled = false
    const today = companyNow()
    const quiet = (p, fallback) => p.catch(() => fallback)
    Promise.all([
      canSeeFinance ? quiet(getKpis({ as_of: isoDay(today) }), null) : null,
      canSeeFinance ? quiet(getKpis({ as_of: isoDay(shiftDays(today, -1)) }), null) : null,
      canSeeFinance ? quiet(getKpis({ as_of: isoDay(sameDayLastMonth(today)) }), null) : null,
      canSeeFinance ? quiet(getTrendDaily({ start: `${today.getFullYear()}-01-01`, end: isoDay(today) }), null) : null,
      canSeeVendors ? quiet(getApAging({ as_of: isoDay(today), limit: 5 }), null) : null,
      canSeeCustomers ? quiet(getArAging({ as_of: isoDay(today), limit: 0 }), null) : null,
      canSeeSupply ? quiet(getLowStock({ threshold: LOW_STOCK_THRESHOLD }), []) : [],
      canSeeSupply ? quiet(getTopProducts({ days: 30, limit: 5 }), []) : [],
    ]).then(([kpis, yesterday, lastMonth, trend, ap, ar, lowStock, topProducts]) => {
      if (cancelled) return
      setData({ kpis, yesterday, lastMonth, trend: trend && byMonth(trend), ap, ar, lowStock, topProducts })
    })
    return () => { cancelled = true }
  }, [canSeeFinance, canSeeVendors, canSeeCustomers, canSeeSupply])

  const today = companyNow()
  const longDate = today.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
  const logoSrc = company?.logo_path
    ? (company.logo_path.startsWith('http') ? company.logo_path : (import.meta.env.VITE_API_URL || '') + company.logo_path)
    : ''

  const actions = [
    canSell && has('pos') && { to: '/sales/pos', label: 'New sale', Icon: Store },
    canSell && has('invoices') && { to: '/sales/quotes/new', label: 'New quote', Icon: FilePlus2 },
    canSell && has('invoices') && { to: '/sales/customer-invoices/new', label: 'New invoice', Icon: FileText },
    canSell && !has('invoices') && has('jobs') && { to: '/jobs/new', label: 'New job', Icon: FilePlus2 },
    canBuy && has('purchase') && { to: '/purchase/po-form/new', label: 'New purchase order', Icon: ShoppingCart },
    canBuy && has('purchase') && has('inventory') && { to: '/purchase/receipts/new', label: 'Receive goods', Icon: Truck },
    canSpend && { to: '/operating/new', label: 'Record an expense', Icon: Receipt },
  ].filter(Boolean)

  const attention = []
  if (approvals.po > 0) {
    attention.push({ to: '/purchase/po-approval-inbox', text: `${approvals.po} ${approvals.po === 1 ? 'PO' : 'POs'} to approve` })
  }
  if (approvals.valuation > 0) {
    attention.push({ to: '/inventory/valuation-approvals', text: `${approvals.valuation} ${approvals.valuation === 1 ? 'valuation' : 'valuations'} to approve` })
  }
  if (approvals.counts > 0) {
    attention.push({ to: '/inventory/counts?show=waiting', text: `${approvals.counts} stock ${approvals.counts === 1 ? 'count' : 'counts'} to approve` })
  }

  const showNumbers = canSeeFinance || canSeeSupply
  const loading = showNumbers && !data

  return (
    <div className="ov">
      <header className="ov-header">
        <div className="ov-company">
          {logoSrc ? (
            <img className="ov-logo" src={logoSrc} alt="" />
          ) : isAdmin && (
            <Link to="/settings/company" className="ov-logo ov-logo-add" title="Add your company logo">Add logo</Link>
          )}
          <div className="ov-company-text">
            <h1>{company?.company_name || 'Overview'}</h1>
            <p className="ov-subtitle">{longDate}</p>
          </div>
        </div>
        {attention.length > 0 && (
          <ul className="ov-attention" aria-label="Waiting for your approval">
            {attention.map((a) => (
              <li key={a.to}>
                <Link to={a.to}><CircleAlert size={14} strokeWidth={2} aria-hidden="true" />{a.text}</Link>
              </li>
            ))}
          </ul>
        )}
      </header>

      {actions.length > 0 && (
        <nav className="ov-actions" aria-label="Quick actions">
          {actions.map(({ to, label, Icon }) => (
            <Link key={to} to={to} className="ov-action">
              <Icon size={16} strokeWidth={1.75} aria-hidden="true" />
              {label}
            </Link>
          ))}
        </nav>
      )}

      {loading && <p className="sum-state">Loading your numbers…</p>}

      {data && showNumbers && <Numbers data={data} canSeeFinance={canSeeFinance} books={books} canSeeVendors={canSeeVendors} canSeeCustomers={canSeeCustomers} canSeeSupply={canSeeSupply} canSeeStock={canSeeStock} />}

      {!showNumbers && actions.length === 0 && (
        <section className="sum-card">
          <p className="sum-muted">There is nothing to show here for your role yet. Use the menu on the left to get started.</p>
        </section>
      )}
    </div>
  )
}

function Numbers({ data, canSeeFinance, books, canSeeSupply, canSeeStock, canSeeVendors, canSeeCustomers }) {
  const { kpis, yesterday, lastMonth, trend, ap, ar, lowStock, topProducts } = data
  const owedToUs = Number(ar?.totals?.balance || 0)
  const lateToUs = Math.max(0, owedToUs - Number(ar?.totals?.current || 0))
  const now = companyNow()
  const lastMonthName = MONTHS[sameDayLastMonth(now).getMonth()]
  const todayVs = comparison(kpis?.sales_today, yesterday?.sales_today, 'yesterday')
  const monthVs = comparison(kpis?.sales_mtd, lastMonth?.sales_mtd, `${lastMonthName} so far`)
  const owed = Number(ap?.totals?.balance || 0)
  const overdue = Math.max(0, owed - Number(ap?.totals?.current || 0))
  const hasTrend = trend && trend.revenue.some((v) => v !== 0)
  const [chartRef, chartWidth] = useWidth()

  return (
    <>
      <div className="sum-tiles">
        {canSeeFinance && (
          <>
            {books && <Tile label="Cash on hand" href="/accounting/reports/balance-sheet" icon={Wallet}
              value={money(kpis?.cash_position)} note="Across your cash and bank accounts" />}
            <Tile label="Sales today" href="/sales/list" icon={Store}
              value={money(kpis?.sales_today)} note={todayVs.note} trend={todayVs.tone} />
            <Tile label={`Sales in ${MONTHS[now.getMonth()]}`} href="/sales/list" icon={CalendarDays}
              value={money(kpis?.sales_mtd)} note={monthVs.note} trend={monthVs.tone} />
            {canSeeVendors && <Tile label="Owed to vendors" href="/purchase/ap-ledger" icon={Truck}
              value={money(owed)} note={overdue > 0 ? `${money(overdue)} past due` : 'Nothing past due'}
              tone={overdue > 0 ? 'warn' : undefined} />}
            {canSeeCustomers && <Tile label="Owed by customers" href="/sales/customer-payments" icon={HandCoins}
              value={money(owedToUs)} note={lateToUs > 0 ? `${money(lateToUs)} past due` : 'Nothing past due'}
              tone={lateToUs > 0 ? 'warn' : undefined} />}
          </>
        )}
        {canSeeSupply && (
          <Tile label="Running low" href={canSeeStock ? '/inventory' : '/dashboard/analysis'} icon={PackageMinus}
            value={String(lowStock.length)}
            note={lowStock.length ? 'At or below their reorder level' : 'Stock levels look fine'}
            tone={lowStock.length ? 'warn' : undefined} />
        )}
      </div>

      <div className="sum-columns">
        <div className="sum-main">
          {canSeeFinance && (
            <Card title={`Sales in ${now.getFullYear()}`} action={books && <Link to="/accounting/reports/income-statement">Income statement</Link>}>
              {hasTrend ? (
                <div ref={chartRef}>
                  {chartWidth > 0 && <MultiBarChart
                    width={chartWidth}
                    height={chartWidth < 480 ? 200 : 240}
                    yTicks={5}
                    paddingLeft={44}
                    paddingRight={4}
                    labels={trend.labels}
                    yFormatter={compact}
                    series={[
                      { name: 'Sales', color: '#2563eb', values: trend.revenue },
                      { name: 'Gross profit', color: '#16a34a', values: trend.gross },
                    ]}
                  />}
                  <p className="sum-legend">
                    <span><i style={{ background: '#2563eb' }} />Sales</span>
                    <span><i style={{ background: '#16a34a' }} />Gross profit (sales less the cost of the goods sold)</span>
                  </p>
                </div>
              ) : (
                <p className="sum-muted">No sales recorded this year yet.</p>
              )}
            </Card>
          )}

          {canSeeSupply && (
            <Card title="Best sellers · last 30 days" action={<Link to="/dashboard/analysis">More analysis</Link>}>
              {topProducts.length ? (
                <ul className="sum-list">
                  {topProducts.map((p) => (
                    <li key={p.product_id ?? p.sku}>
                      <span className="sum-list-main">
                        {p.name || p.sku}
                        {p.sku && p.name && <span className="sum-muted"> · {p.sku}</span>}
                      </span>
                      <span className="sum-list-side">{money(p.revenue)} <span className="sum-muted">· {Number(p.qty_sold || 0).toLocaleString()} sold</span></span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="sum-muted">No sales in the last 30 days.</p>
              )}
            </Card>
          )}
        </div>

        <div className="sum-side">
          {canSeeSupply && (
            <Card title="Running low" action={canSeeStock && <Link to="/inventory">Inventory</Link>}>
              {lowStock.length ? (
                <ul className="sum-list">
                  {lowStock.slice(0, 6).map((p) => (
                    <li key={p.product_id ?? p.sku}>
                      <span className="sum-list-main">{canSeeStock && p.product_id ? <Link to={`/inventory/stock/${p.product_id}`}>{p.name || p.sku}</Link> : (p.name || p.sku)}</span>
                      <span className={`sum-list-side${Number(p.qty_on_hand) <= 0 ? ' sum-danger' : ''}`}>
                        {Number(p.qty_on_hand) <= 0 ? 'Out of stock' : `${Number(p.qty_on_hand).toLocaleString()} left`}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="sum-muted">Nothing is running low.</p>
              )}
              {lowStock.length > 6 && (
                <p className="sum-more"><Link to="/dashboard/analysis">{lowStock.length - 6} more <ArrowRight size={14} aria-hidden="true" /></Link></p>
              )}
            </Card>
          )}

          {canSeeVendors && (
            <Card title="Owed to vendors" action={<Link to="/purchase/ap-ledger">All vendors</Link>}>
              {ap?.vendors?.length ? (
                <ul className="sum-list">
                  {ap.vendors.map((v) => {
                    const pastDue = Math.max(0, Number(v.balance || 0) - Number(v.current || 0))
                    return (
                      <li key={v.vendor_id ?? v.vendor_name}>
                        <span className="sum-list-main">
                          {v.vendor_id ? <Link to={`/purchase/ap/vendor/${v.vendor_id}`}>{v.vendor_name || v.vendor_id}</Link> : v.vendor_name}
                          {pastDue > 0 && <span className="sum-warn"> · {money(pastDue)} past due</span>}
                        </span>
                        <span className="sum-list-side">{money(v.balance)}</span>
                      </li>
                    )
                  })}
                </ul>
              ) : (
                <p className="sum-muted">You don't owe any vendors.</p>
              )}
            </Card>
          )}

          {canSeeFinance && !canSeeSupply && (
            <p className="sum-more"><Link to="/dashboard/analysis"><TrendingUp size={14} aria-hidden="true" /> More analysis</Link></p>
          )}
        </div>
      </div>
    </>
  )
}
